import { registerYAxis, type AxisTick } from 'klinecharts'

/**
 * "Fixed price grid" — a custom klinecharts y-axis whose ticks land on a fixed
 * price increment (every $0.25, $1, …) instead of klinecharts' nice-number
 * auto ticks.
 *
 * ONE AXIS TEMPLATE PER INCREMENT. klinecharts registers axis templates in a
 * global, name-keyed registry and `createTicks` receives no chart/pane
 * identity, so a module-level "current increment" would be last-writer-wins
 * across every chart in a grid (and stale, since ticks are only rebuilt when
 * the axis NAME changes or the range moves). Keying the template by increment
 * makes each chart's pane select its own axis by name, which also forces
 * klinecharts to recreate the axis component when the increment changes.
 *
 * NB: the ticks below are computed in RAW PRICE space and assume a top-down
 * axis. klinecharts' range is in the axis' internal space (log10 values for a
 * logarithmic scale, percent for a percentage scale) and flips the coord
 * mapping when `yAxis.reverse` is on — neither is visible to `createTicks`, so
 * the caller (useChartStyles) only selects this axis for a regular,
 * non-inverted scale.
 */

const FIXED_PRICE_AXIS_PREFIX = 'fixed_price'

/** Minimum vertical gap between two fixed ticks before the stride is widened. */
const MIN_TICK_SPACING_PX = 16

/**
 * Smallest 1-2-5-decade multiple of the increment that is at least `minimum`.
 *
 * The widened stride has to stay a ROUND multiple: `Math.ceil(minimum)` would
 * satisfy the spacing with e.g. 46x, drawing a $0.01 grid at $0.46 intervals —
 * legible, but the labels are junk and the whole point of a fixed grid is round
 * levels. The 1-2-5 ladder gives 50x → $0.50 instead.
 */
function strideMultiplier(minimum: number): number {
  if (minimum <= 1) return 1
  const decade = 10 ** Math.floor(Math.log10(minimum))
  for (const m of [1, 2, 5]) {
    if (m * decade >= minimum) return m * decade
  }
  return 10 * decade
}

/** Decimal places in a number's literal form — 0.25 → 2, 5 → 0. */
function getPrecision(value: number): number {
  const str = value.toString()
  const idx = str.indexOf('.')
  return idx === -1 ? 0 : str.length - idx - 1
}

export interface FixedPriceTickParams {
  /** The user's price increment (chart setting). */
  increment: number
  /** Axis range low / high, in price. */
  from: number
  to: number
  /** Pixel height of the y-axis strip. */
  height: number
  /** klinecharts' auto ticks — returned unchanged when a fixed grid can't be drawn. */
  defaultTicks: AxisTick[]
}

/**
 * Pure tick generator — the testable seam of the fixed price axis.
 *
 * Falls back to `defaultTicks` for a non-positive increment, a degenerate range
 * or a zero-height axis (all of which are transient states during mount /
 * resize, not user error).
 */
export function fixedPriceTicks({
  increment,
  from,
  to,
  height,
  defaultTicks,
}: FixedPriceTickParams): AxisTick[] {
  const priceRange = to - from
  if (!(increment > 0) || !(priceRange > 0) || !(height > 0)) return defaultTicks

  // Widen the stride to a whole multiple of the increment when the requested
  // grid would be denser than the labels can render. A $0.01 grid over a $200
  // range is 20k ticks: unreadable, and a canvas stall on every repaint.
  const pxPerIncrement = height * (increment / priceRange)
  const step = strideMultiplier(MIN_TICK_SPACING_PX / pxPerIncrement)
  const stride = increment * step

  const precision = getPrecision(increment)
  const ticks: AxisTick[] = []
  const startIndex = Math.ceil(from / stride)
  const endIndex = Math.floor(to / stride)
  for (let i = startIndex; i <= endIndex; i++) {
    // Recompute from the index rather than accumulating — repeated `+= stride`
    // drifts (0.1 + 0.2 …) and shows up as 12.300000000000001 labels.
    const price = Number((i * stride).toFixed(precision))
    const ratio = (price - from) / priceRange
    ticks.push({ coord: height * (1 - ratio), value: price, text: price.toFixed(precision) })
  }
  return ticks.length > 0 ? ticks : defaultTicks
}

const registered = new Set<string>()

/**
 * Register (once) the fixed-price axis for `increment` and return its
 * klinecharts axis name, for `setPaneOptions({ axisOptions: { name } })`.
 */
export function ensureFixedPriceAxis(increment: number): string {
  const name = `${FIXED_PRICE_AXIS_PREFIX}_${increment}`
  if (registered.has(name)) return name
  registerYAxis({
    name,
    createTicks: ({ range, bounding, defaultTicks }) =>
      fixedPriceTicks({
        increment,
        from: range.from,
        to: range.to,
        height: bounding.height,
        defaultTicks,
      }),
  })
  registered.add(name)
  return name
}
